/**
 * Reactions — the alchemy engine.
 *
 * Two contacts per cell (right and down) means every neighbour pair in a dirty
 * rect is visited exactly once per pass. For each pair the engine asks: is
 * there a rule for this ordered pair of materials?
 *
 *   1. concrete MatId×MatId rules, `Map<(a<<16)|b, …>` — O(1), checked first
 *   2. tag rules, reached through a per-material candidate index built at
 *      compile time so a contact never scans rules it cannot match
 *
 * The first rule that passes its conditions and wins its dice roll fires, and
 * the contact is done. That cap is what keeps a chain reaction from turning one
 * cell into a dozen transmutes in a single tick.
 *
 * Tag masks match with ANY-of semantics: a material satisfies a mask if it
 * carries at least one of its bits. Conjunctions are expressed as separate
 * rules; the one negation the spec needs (`VOID` × any non-`VOID`) is handled
 * by an explicit engine invariant below.
 *
 * Three invariants live in the engine rather than in the rule table, because a
 * rule that forgot one would be a bug rather than a design choice:
 *   - nothing gains `BURNING` below its own `ignitionTemp`
 *   - a cell that becomes `WET` stops burning
 *   - corrosion chance scales by attacker `corrosiveness` × defender
 *     (1 − `corrosionResist`), which is why `CORROSIVE` × `CERAMIC` is silent
 *     without a rule saying so — ceramic's resist is 1.0
 */

import type { Rng } from '../core/rng';
import { Flag, M, Tag, VOID, materialCount, type MatId } from './materials';
import { CHUNK_MASK, CHUNK_SHIFT, type CellWorld } from './world';

export interface ReactionRule {
  a: number; // MatId if `aIsTag` false, else a TagMask
  b: number;
  aIsTag: boolean;
  bIsTag: boolean;
  chance: number; // 0..1 per contact per tick
  minTemp?: number;
  maxTemp?: number;
  requiresFlagA?: number;
  requiresFlagB?: number;
  forbidsFlagA?: number;
  forbidsFlagB?: number;
  outA?: number | 'keep' | 'consume'; // MatId | keep | consume(→VOID)
  outB?: number | 'keep' | 'consume';
  heat?: number; // degrees injected at contact
  flagA?: number;
  flagB?: number;
  emit?: { mat: number; count: number; velocity?: number };
  charge?: number;
  event?: string; // codex discovery id
  priority?: number; // concrete pairs beat tag rules
}

/** Sentinel for `outA`/`outB` meaning "leave this cell's material alone". */
const KEEP = -1;

const TEMP_MIN = -32768;
const TEMP_MAX = 32767;


interface Compiled {
  a: number;
  b: number;
  aIsTag: boolean;
  bIsTag: boolean;
  chance: number;
  minTemp: number;
  maxTemp: number;
  reqA: number;
  reqB: number;
  forbidA: number;
  forbidB: number;
  outA: number;
  outB: number;
  heat: number;
  flagA: number;
  flagB: number;
  emitMat: number;
  emitCount: number;
  emitRadius: number;
  charge: number;
  event: string | null;
  priority: number;
  /** Precomputed gates so the hot path skips work it does not need. */
  needsTemp: boolean;
  needsFlags: boolean;
  corrosive: boolean;
  voidErase: boolean;
}

function resolveOut(v: number | 'keep' | 'consume' | undefined): number {
  if (v === undefined || v === 'keep') return KEEP;
  if (v === 'consume') return VOID;
  return v;
}

function sideMatches(mat: MatId, side: number, isTag: boolean): boolean {
  return isTag ? (M.tags[mat] & side) !== 0 : mat === side;
}

export class ReactionEngine {
  private readonly world: CellWorld;
  private readonly rng: Rng;

  /** Concrete pairs, keyed `(a<<16)|b`. Values are priority-sorted lists. */
  private pairMap = new Map<number, Compiled[]>();
  private tagRules: Compiled[] = [];
  /** Per-material indices into `tagRules` for rules this material can fill A of. */
  private tagCandidates: Uint16Array[] = [];
  /** Materials that appear on either side of any rule. Fast contact rejection. */
  private participates = new Uint8Array(0);

  /** Resumable cursor: where the last budgeted `step` stopped. */
  private cursorChunk = 0;
  private cursorY = -1;

  private discovered = new Set<string>();

  /**
   * Deterministic work quota, in cells scanned per tick.
   *
   * Replaces a `performance.now()` budget, which was a direct violation of the
   * determinism guarantee in docs/01 §8 — the same seed processed a different
   * number of contact pairs on a faster CPU and the two runs diverged. The
   * irony was that `cellsim.ts:44-53` quotes that section at length while its
   * neighbour scheduled off the wall clock.
   *
   * Calibrated against the 1.2 ms budget in docs/01 §9: a scanned cell measures
   * 60–96 ns across the stress scenes (Node 22, darwin/arm64), so 13000 cells
   * is 0.8–1.25 ms — and above the ~9700 the loudest fixture asks for, so the
   * quota bounds the worst case without throttling the normal one.
   */
  maxCellsPerTick = 13000;

  /** Cells scanned this tick. Telemetry. */
  cellsThisTick = 0;

  /** First time a given `event` fires, ever. Feeds the codex. */
  onDiscovery: ((event: string, x: number, y: number, a: number, b: number) => void) | null = null;

  /**
   * Every time a reaction with an `event` id fires, not just the first.
   *
   * `onDiscovery` is deliberately once-per-profile — that is what a codex is —
   * which makes it the wrong signal for a live event ticker: a player would see
   * "fuel air explosion" once and never again, including in later runs.
   */
  onReaction: ((event: string, x: number, y: number) => void) | null = null;

  constructor(world: CellWorld, rng: Rng) {
    this.world = world;
    this.rng = rng;
  }

  // ------------------------------------------------------------- compile

  /** Compile the rule list into lookup structures. Call once after materials finalize. */
  compile(rules: ReactionRule[]): void {
    this.pairMap.clear();
    this.tagRules = [];

    const n = materialCount();
    this.participates = new Uint8Array(n);

    for (const rule of rules) {
      const c = this.compileOne(rule);
      for (let m = 0; m < n; m++) {
        if (sideMatches(m, c.a, c.aIsTag) || sideMatches(m, c.b, c.bIsTag)) {
          this.participates[m] = 1;
        }
      }
      if (!c.aIsTag && !c.bIsTag) {
        const key = (c.a << 16) | c.b;
        const list = this.pairMap.get(key);
        // Same-pair rules coexist in one bucket; a second rule for a pair
        // refines the first rather than replacing it.
        if (list === undefined) this.pairMap.set(key, [c]);
        else list.push(c);
      } else {
        this.tagRules.push(c);
      }
    }

    for (const list of this.pairMap.values()) sortByPriority(list);

    this.tagCandidates = new Array(n);
    const scratch: number[] = [];
    for (let m = 0; m < n; m++) {
      scratch.length = 0;
      for (let i = 0; i < this.tagRules.length; i++) {
        const r = this.tagRules[i];
        if (sideMatches(m, r.a, r.aIsTag)) scratch.push(i);
      }
      // Declaration order breaks priority ties, so the table reads top-down.
      scratch.sort((x, y) => this.tagRules[y].priority - this.tagRules[x].priority || x - y);
      this.tagCandidates[m] = new Uint16Array(scratch);
    }

    this.cursorChunk = 0;
    this.cursorY = -1;
  }

  private compileOne(r: ReactionRule): Compiled {
    const flagA = r.flagA ?? 0;
    const flagB = r.flagB ?? 0;
    const minTemp = r.minTemp ?? TEMP_MIN;
    const maxTemp = r.maxTemp ?? TEMP_MAX;
    const reqA = r.requiresFlagA ?? 0;
    const reqB = r.requiresFlagB ?? 0;
    const forbidA = r.forbidsFlagA ?? 0;
    const forbidB = r.forbidsFlagB ?? 0;
    const velocity = r.emit?.velocity ?? 0;

    return {
      a: r.a,
      b: r.b,
      aIsTag: r.aIsTag,
      bIsTag: r.bIsTag,
      chance: r.chance,
      minTemp,
      maxTemp,
      reqA,
      reqB,
      forbidA,
      forbidB,
      outA: resolveOut(r.outA),
      outB: resolveOut(r.outB),
      heat: r.heat ?? 0,
      flagA,
      flagB,
      emitMat: r.emit?.mat ?? VOID,
      emitCount: r.emit?.count ?? 0,
      emitRadius: velocity >= 1 ? 1 + Math.min(2, Math.floor(velocity)) : 1,
      charge: r.charge ?? 0,
      event: r.event ?? null,
      priority: r.priority ?? 0,
      needsTemp:
        minTemp !== TEMP_MIN || maxTemp !== TEMP_MAX || ((flagA | flagB) & Flag.BURNING) !== 0,
      needsFlags: (reqA | reqB | forbidA | forbidB) !== 0,
      corrosive: r.aIsTag && (r.a & Tag.CORROSIVE) !== 0,
      voidErase: r.aIsTag && r.bIsTag && (r.a & Tag.VOID) !== 0,
    };
  }

  // ---------------------------------------------------------------- step

  /**
   * Process contacts in a chunk's dirty rect, bounded by a deterministic cell
   * quota.
   *
   * `_budgetMs` is retained only so existing call sites keep compiling and is
   * ignored — the same shape `charge.step` and `pressure.step` already have.
   * Throughput is `maxCellsPerTick`; change that, not this argument.
   */
  step(): void {
    this.cellsThisTick = 0;
    const chunks = this.world.activeChunks;
    if (chunks.length === 0 || this.participates.length === 0) {
      this.cursorChunk = 0;
      this.cursorY = -1;
      return;
    }
    if (this.cursorChunk >= chunks.length) {
      this.cursorChunk = 0;
      this.cursorY = -1;
    }

    let budget = this.maxCellsPerTick;
    let resumeY = this.cursorY;

    for (let ci = this.cursorChunk; ci < chunks.length; ci++) {
      const c = chunks[ci];
      const y0 = resumeY >= 0 && resumeY > c.dirtyMinY ? resumeY : c.dirtyMinY;
      resumeY = -1;
      if (!c.hasDirty()) continue;

      const baseX = c.cx << CHUNK_SHIFT;
      const baseY = c.cy << CHUNK_SHIFT;
      const x0 = c.dirtyMinX;
      const x1 = c.dirtyMaxX;
      const width = x1 - x0 + 1;

      for (let ly = y0; ly <= c.dirtyMaxY; ly++) {
        const row = ly << CHUNK_SHIFT;
        const wy = baseY + ly;
        for (let lx = x0; lx <= x1; lx++) {
          const i = row | lx;
          const wx = baseX + lx;

          let m = c.mat[i];
          if (this.canReact(m)) {
            const right = lx < CHUNK_MASK ? c.mat[i + 1] : this.world.peek(wx + 1, wy);
            if (this.canReact(right)) this.contact(wx, wy, m, wx + 1, wy, right);
          }
          // The right-hand contact may have transmuted this cell.
          m = c.mat[i];
          if (this.canReact(m)) {
            const down =
              ly < CHUNK_MASK ? c.mat[i + (1 << CHUNK_SHIFT)] : this.world.peek(wx, wy + 1);
            if (this.canReact(down)) this.contact(wx, wy, m, wx, wy + 1, down);
          }
        }

        budget -= width;
        this.cellsThisTick += width;
        if (budget <= 0) {
          this.cursorChunk = ci;
          this.cursorY = ly + 1;
          return;
        }
      }
    }

    this.cursorChunk = 0;
    this.cursorY = -1;
  }

  private canReact(m: MatId): boolean {
    return m !== VOID && this.participates[m] === 1;
  }

  // ------------------------------------------------------------- matching

  private contact(x1: number, y1: number, m1: MatId, x2: number, y2: number, m2: MatId): void {
    const forward = this.pairMap.get((m1 << 16) | m2);
    if (forward !== undefined && this.tryList(forward, x1, y1, m1, x2, y2, m2)) return;
    if (m1 !== m2) {
      const reverse = this.pairMap.get((m2 << 16) | m1);
      if (reverse !== undefined && this.tryList(reverse, x2, y2, m2, x1, y1, m1)) return;
    }

    // A concrete pair silences the tag layer entirely — that is what "concrete
    // overrides tag" means, and it is why the acid two-stage rules can pre-empt
    // the broad CORROSIVE × METAL rule.
    const asA = this.tagCandidates[m1];
    for (let k = 0; k < asA.length; k++) {
      const r = this.tagRules[asA[k]];
      if (!sideMatches(m2, r.b, r.bIsTag)) continue;
      if (this.tryRule(r, x1, y1, m1, x2, y2, m2)) return;
    }
    if (m1 === m2) return;
    const asB = this.tagCandidates[m2];
    for (let k = 0; k < asB.length; k++) {
      const r = this.tagRules[asB[k]];
      if (!sideMatches(m1, r.b, r.bIsTag)) continue;
      if (this.tryRule(r, x2, y2, m2, x1, y1, m1)) return;
    }
  }

  private tryList(
    list: Compiled[],
    ax: number,
    ay: number,
    am: MatId,
    bx: number,
    by: number,
    bm: MatId,
  ): boolean {
    for (let k = 0; k < list.length; k++) {
      if (this.tryRule(list[k], ax, ay, am, bx, by, bm)) return true;
    }
    return false;
  }

  private tryRule(
    r: Compiled,
    ax: number,
    ay: number,
    am: MatId,
    bx: number,
    by: number,
    bm: MatId,
  ): boolean {
    // "any non-VOID" from §5.1 is a negation that ANY-of tag masks cannot
    // express, so voidglass does not erase void mist here instead.
    if (r.voidErase && (M.tags[bm] & Tag.VOID) !== 0) return false;

    if (r.needsFlags) {
      const fa = this.world.getFlags(ax, ay);
      const fb = this.world.getFlags(bx, by);
      if ((fa & r.reqA) !== r.reqA) return false;
      if ((fb & r.reqB) !== r.reqB) return false;
      if ((fa & r.forbidA) !== 0) return false;
      if ((fb & r.forbidB) !== 0) return false;
    }

    if (r.needsTemp) {
      const ta = this.world.getTemp(ax, ay);
      const tb = this.world.getTemp(bx, by);
      // `minTemp` asks whether the contact is hot enough anywhere and `maxTemp`
      // whether it is cold enough anywhere: the extreme cell drives the change.
      if ((ta > tb ? ta : tb) < r.minTemp) return false;
      if ((ta < tb ? ta : tb) > r.maxTemp) return false;
      if ((r.flagA & Flag.BURNING) !== 0 && ta < M.ignitionTemp[am]) return false;
      if ((r.flagB & Flag.BURNING) !== 0 && tb < M.ignitionTemp[bm]) return false;
    }

    let p = r.chance * M.reactivity[am] * M.reactivity[bm];
    if (r.corrosive) p *= M.corrosiveness[am] * (1 - M.corrosionResist[bm]);
    if (p <= 0) return false;
    if (p < 1 && !this.rng.chance(p)) return false;

    this.apply(r, ax, ay, am, bx, by, bm);
    return true;
  }

  // -------------------------------------------------------------- effects

  private apply(
    r: Compiled,
    ax: number,
    ay: number,
    am: MatId,
    bx: number,
    by: number,
    bm: MatId,
  ): void {
    const w = this.world;

    if (r.outA !== KEEP) w.transmute(ax, ay, r.outA);
    if (r.outB !== KEEP) w.transmute(bx, by, r.outB);

    if (r.heat !== 0) {
      w.addTemp(ax, ay, r.heat);
      w.addTemp(bx, by, r.heat);
    }

    if (r.flagA !== 0) {
      w.addFlag(ax, ay, r.flagA);
      if ((r.flagA & Flag.WET) !== 0) w.clearFlag(ax, ay, Flag.BURNING);
    }
    if (r.flagB !== 0) {
      w.addFlag(bx, by, r.flagB);
      if ((r.flagB & Flag.WET) !== 0) w.clearFlag(bx, by, Flag.BURNING);
    }

    if (r.charge > 0) {
      // Magnitude and propagation belong to charge.ts; the flag is the handoff.
      if (w.peek(ax, ay) !== VOID) w.addFlag(ax, ay, Flag.CHARGED);
      if (w.peek(bx, by) !== VOID) w.addFlag(bx, by, Flag.CHARGED);
    }

    if (r.emitCount > 0) this.emit(r, ax, ay);

    if (r.event !== null) {
      if (this.onReaction !== null) this.onReaction(r.event, ax, ay);
      if (!this.discovered.has(r.event)) {
        this.discovered.add(r.event);
        if (this.onDiscovery !== null) this.onDiscovery(r.event, ax, ay, am, bm);
      }
    }
  }

  /** Scatter emitted matter into empty cells near the contact. */
  private emit(r: Compiled, x: number, y: number): void {
    const w = this.world;
    const rad = r.emitRadius;
    let placed = 0;
    let attempts = r.emitCount * 6;
    while (placed < r.emitCount && attempts-- > 0) {
      const ox = this.rng.int(-rad, rad);
      const oy = this.rng.int(-rad, rad);
      if (ox === 0 && oy === 0) continue;
      const px = x + ox;
      const py = y + oy;
      if (w.peek(px, py) !== VOID) continue;
      w.set(px, py, r.emitMat);
      placed++;
    }
  }

  // ----------------------------------------------------------- inspection

  /** Discovery ids fired so far. Save/load hands this to the codex. */
  discoveries(): ReadonlySet<string> {
    return this.discovered;
  }

  /** Restore discovery state so a loaded game does not re-fire the codex. */
  seedDiscoveries(events: Iterable<string>): void {
    for (const e of events) this.discovered.add(e);
  }

  /** Counts for the boot log and for tests. */
  stats(): { concretePairs: number; concreteRules: number; tagRules: number } {
    let concreteRules = 0;
    for (const list of this.pairMap.values()) concreteRules += list.length;
    return {
      concretePairs: this.pairMap.size,
      concreteRules,
      tagRules: this.tagRules.length,
    };
  }
}

function sortByPriority(list: Compiled[]): void {
  list.sort((a, b) => b.priority - a.priority);
}
